/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import parameters, {
	type useSiteSkillsConnectionQuery,
} from '@jira/servicedesk__skills/src/__generated__/useSiteSkillsConnectionQuery.graphql.ts';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import siteWfoStatusParameters from './__generated__/ProductsJsmWorkforceSkillsPageSiteWfoStatusQuery$parameters';
import type { ProductsJsmWorkforceSkillsPageSiteWfoStatusQuery } from './__generated__/ProductsJsmWorkforceSkillsPageSiteWfoStatusQuery.graphql.ts';

// Matches the page size used inside `useSiteSkillsConnection` so the preloaded
// query and the in-page `loadMoreCategories` paginate by the same step.
const CATEGORIES_PAGE_SIZE = 10;
const SERVICE_DESK = 'jira-servicedesk';
const FREE_EDITION = 'FREE_EDITION';
const STANDARD_EDITION = 'STANDARD_EDITION';

const hasLicensedServiceDeskAboveStandard = ({
	licensedProducts,
	serviceDeskEdition,
}: {
	licensedProducts?: Record<string, boolean | undefined>;
	serviceDeskEdition?: string;
}) =>
	Boolean(licensedProducts?.[SERVICE_DESK]) &&
	serviceDeskEdition !== undefined &&
	serviceDeskEdition !== FREE_EDITION &&
	serviceDeskEdition !== STANDARD_EDITION;

type ProductsJsmWorkforceSkillsEntryPointComponent = EntryPointComponent<
	{
		siteSkillsQueryRef: useSiteSkillsConnectionQuery;
		siteWfoStatusQueryRef: ProductsJsmWorkforceSkillsPageSiteWfoStatusQuery;
	},
	{},
	{},
	{ cloudId: string }
>;

export const productsJsmWorkforceSkillsEntryPoint: EntryPoint<
	ProductsJsmWorkforceSkillsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<ProductsJsmWorkforceSkillsEntryPointComponent>(
		/* webpackChunkName: "async-products-jsm-workforce-skills", jiraSpaEntry: "async-products-jsm-workforce-skills" */ './ProductsJsmWorkforceSkillsPage.tsx',
	),
	getPreloadProps: ({
		tenantContext: { cloudId, appEditions, isAdmin, licensedProducts },
	}: EntryPointRouteParams) => ({
		queries: {
			siteWfoStatusQueryRef: {
				parameters: siteWfoStatusParameters,
				variables: {
					cloudId,
					skipSiteWfoStatus: !(
						isAdmin &&
						hasLicensedServiceDeskAboveStandard({
							licensedProducts,
							serviceDeskEdition: appEditions?.serviceDesk,
						}) &&
						fg('jsm_wfo_skills')
					),
				},
				options: {
					fetchPolicy: 'store-or-network' as const,
				},
			},
			siteSkillsQueryRef: {
				parameters,
				variables: {
					cloudId,
					first: CATEGORIES_PAGE_SIZE,
				},
			},
		},
		extraProps: { cloudId },
	}),
});
